import JSZip from 'jszip';
import { ensurePdfjsWorker, pdfjsLib } from '@/utils/pdfjsWorker';

export type RagFilePreviewResult =
  | { kind: 'image'; dataUrl: string; subtitle: string }
  | { kind: 'text'; content: string; subtitle: string };

const MARKDOWN_FIRST_PAGE_LINES = 48;
const TEXT_PREVIEW_MAX_CHARS = 2400;

function fileExtensionLower(filename: string): string {
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot) : '';
}

async function renderPdfFirstPage(file: File): Promise<RagFilePreviewResult> {
  ensurePdfjsWorker();
  const data = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(data) });
  const pdf = await loadingTask.promise;
  try {
    if (pdf.numPages < 1) {
      throw new Error('PDF 没有可预览的页面');
    }
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1.25 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('无法创建预览画布');
    }
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    const subtitle = pdf.numPages > 1 ? `第 1 页 / 共 ${pdf.numPages} 页` : '第 1 页';
    return { kind: 'image', dataUrl: canvas.toDataURL('image/png'), subtitle };
  } finally {
    await pdf.destroy();
  }
}

function takeFirstPageText(
  text: string,
  maxLines: number
): { content: string; truncated: boolean } {
  const lines = text.split(/\r?\n/);
  const truncated = lines.length > maxLines;
  const content = lines.slice(0, maxLines).join('\n');
  return { content, truncated };
}

async function previewMarkdownFile(file: File): Promise<RagFilePreviewResult> {
  const text = await file.text();
  const { content, truncated } = takeFirstPageText(text, MARKDOWN_FIRST_PAGE_LINES);
  const subtitle = truncated ? `前 ${MARKDOWN_FIRST_PAGE_LINES} 行（预览）` : '全文预览';
  return { kind: 'text', content: content || '（空文件）', subtitle };
}

function extractTextFromDocxXml(xml: string): string {
  return xml
    .replace(/<w:tab[^>]*\/>/g, '\t')
    .replace(/<w:br[^>]*\/>/g, '\n')
    .replace(/<\/w:p>/g, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCharCode(Number(code)))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

async function previewDocxFile(file: File): Promise<RagFilePreviewResult> {
  const buffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buffer);
  const docXml = zip.file('word/document.xml');
  if (!docXml) {
    throw new Error('无法读取 DOCX 文档内容');
  }
  const xml = await docXml.async('string');
  const fullText = extractTextFromDocxXml(xml);
  const previewText =
    fullText.length > TEXT_PREVIEW_MAX_CHARS
      ? `${fullText.slice(0, TEXT_PREVIEW_MAX_CHARS)}\n…`
      : fullText;
  const subtitle =
    fullText.length > TEXT_PREVIEW_MAX_CHARS ? '首页内容预览（已截断）' : '首页内容预览';
  return { kind: 'text', content: previewText || '（空文档）', subtitle };
}

export async function previewRagUploadFileFirstPage(file: File): Promise<RagFilePreviewResult> {
  const ext = fileExtensionLower(file.name);
  switch (ext) {
    case '.pdf':
      return renderPdfFirstPage(file);
    case '.md':
    case '.markdown':
      return previewMarkdownFile(file);
    case '.docx':
      return previewDocxFile(file);
    default:
      throw new Error('暂不支持预览该文件类型');
  }
}
